import { useState } from 'react'
import { ApiError, checkVoucher } from '../api/client'
import type { VoucherStatus } from '../api/types'
import { useT } from '../i18n'
import { usePriceFormat } from '../shopConfig'

/**
 * Wertgutscheine an der Kasse.
 *
 * # Warum hier und nicht im Warenkorb
 *
 * Ein Wertgutschein mindert keinen Preis — er bezahlt. Die Ware kostet, was
 * sie kostet, die Umsatzsteuer bleibt auf dem vollen Betrag stehen, und der
 * Gutschein begleicht einen Teil der Rechnung. Im Warenkorb sähe er aus wie
 * ein Rabatt, und genau diese Verwechslung führt am Jahresende zu falsch
 * ausgewiesener Steuer.
 *
 * Deshalb steht er im letzten Schritt, bei den Zahlungsarten: Dort gehört er
 * hin, weil er einer ist.
 *
 * # Warum hier nur nachgesehen wird
 *
 * Das Guthaben wird erst beim Bestellen gezogen, und zwar in derselben
 * Transaktion wie die Bestellung. Würde hier schon abgebucht, verlöre jemand,
 * der die Kasse wieder verlässt, sein Guthaben an eine Bestellung, die es nie
 * gab.
 *
 * Gezeigt wird trotzdem sofort, was darauf liegt — sonst gäbe der Kunde einen
 * Code ein und erführe erst nach dem verbindlichen Bestellen, ob er zieht.
 */
export function Gutscheinfeld({
  gutscheine,
  onGutscheine,
  offenerBetrag,
}: {
  gutscheine: VoucherStatus[]
  onGutscheine: (neu: VoucherStatus[]) => void
  /** Was ohne Gutschein zu zahlen wäre — für „deckt die Bestellung". */
  offenerBetrag: number
}) {
  const t = useT()
  const preis = usePriceFormat()

  const [offen, setOffen] = useState(false)
  const [eingabe, setEingabe] = useState('')
  const [prueft, setPrueft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  async function hinzufuegen() {
    const code = eingabe.trim().toUpperCase()
    if (code === '' || prueft) return

    // Denselben Gutschein zweimal wäre keine zweite Zahlung, sondern dieselbe
    // Karte doppelt gezählt — und der Kunde glaubte, mehr gedeckt zu haben.
    if (gutscheine.some((g) => g.code === code.replace(/[^0-9A-Z]/g, ''))) {
      setFehler(t('gutschein.schonDa'))
      return
    }

    setPrueft(true)
    setFehler(null)
    try {
      const stand = await checkVoucher(code)
      if (!stand.usable) {
        // Der Satz des Servers unverändert: Er sagt, ob gesperrt, abgelaufen
        // oder aufgebraucht — drei verschiedene Auskünfte für den Kunden.
        setFehler(stand.reason || t('gutschein.nichtEinloesbar'))
        return
      }
      onGutscheine([...gutscheine, stand])
      setEingabe('')
    } catch (ausnahme) {
      setFehler(
        ausnahme instanceof ApiError && ausnahme.isNotFound
          ? t('gutschein.unbekannt')
          : t('gutschein.fehler'),
      )
    } finally {
      setPrueft(false)
    }
  }

  const gedeckt = gutscheine.reduce((summe, g) => summe + g.balance, 0)
  const rest = Math.max(0, Math.round((offenerBetrag - gedeckt) * 100) / 100)

  if (!offen && gutscheine.length === 0) {
    return (
      <button className="verweisknopf rabatt-auf" type="button" onClick={() => setOffen(true)}>
        {t('gutschein.haben')}
      </button>
    )
  }

  return (
    <div className="gutscheine">
      {gutscheine.map((g) => (
        <div className="rabatt-gilt" key={g.code}>
          <span className="rabatt-marke">{g.code}</span>
          <span className="rabatt-name">{t('gutschein.guthaben')}</span>
          <strong className="rabatt-betrag">{preis(g.balance)}</strong>
          <button
            className="verweisknopf"
            type="button"
            onClick={() => onGutscheine(gutscheine.filter((andere) => andere.code !== g.code))}
          >
            {t('rabatt.entfernen')}
          </button>
        </div>
      ))}

      {/* Was nach den Gutscheinen noch offen ist — die Zahl, wegen der jemand
          einen zweiten heraussucht oder eben doch die Karte zückt. */}
      {gutscheine.length > 0 && (
        <p className="hinweis" role="status">
          {rest > 0
            ? t('gutschein.restOffen', { betrag: preis(rest) })
            : t('gutschein.decktAlles')}
        </p>
      )}

      {/* Ein <div> und kein <form>.
          
          Dieses Feld steht an der Kasse INNERHALB des Bestellformulars, und
          ein <form> im <form> ist ungültiges HTML: Der Browser wirft das
          innere weg. Ein Knopf mit type="submit" löste dann das äußere aus —
          wer einen Gutscheincode eintippt und Enter drückt, hätte damit die
          Bestellung ausgelöst. Genau das ist beim Nachmessen passiert.
          
          Also: kein Formular, ein Knopf vom Typ "button", und die
          Eingabetaste wird hier selbst behandelt. Damit lässt sich das Feld
          überall einsetzen, ohne zu wissen, was darüber steht. */}
      <div className="rabatt">
        <label className="nur-fuer-screenreader" htmlFor="gutscheincode">
          {t('gutschein.label')}
        </label>
        <input
          id="gutscheincode"
          className="rabatt-eingabe"
          type="text"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          placeholder={t('gutschein.platzhalter')}
          value={eingabe}
          disabled={prueft}
          onChange={(ereignis) => {
            setEingabe(ereignis.target.value.toUpperCase())
            setFehler(null)
          }}
          onKeyDown={(ereignis) => {
            if (ereignis.key !== 'Enter') return
            // Aufhalten, bevor es das Formular darüber erreicht.
            ereignis.preventDefault()
            ereignis.stopPropagation()
            void hinzufuegen()
          }}
          aria-describedby={fehler ? 'gutschein-fehler' : undefined}
          aria-invalid={fehler != null || undefined}
        />
        <button
          className="knopf knopf-schlicht"
          type="button"
          disabled={prueft || eingabe.trim() === ''}
          onClick={() => void hinzufuegen()}
        >
          {prueft ? t('gutschein.prueft') : t('gutschein.hinzufuegen')}
        </button>

        {fehler && (
          <p className="hinweis hinweis-fehler rabatt-problem" id="gutschein-fehler" role="alert">
            {fehler}
          </p>
        )}
      </div>
    </div>
  )
}
